/**
 * Founder Control Center v2 · Dashboard Principal.
 *
 * Único punto de entrada del founder tras login.
 *
 * Estructura (V2 spec · `FOUNDER_CONTROL_CENTER_V2.md` §2):
 *   1. Hero
 *   2. North Star sticky (con picker inline para reconfigurar)
 *   3. Salud Plataforma (Health Score + 4 sub-scores)
 *   4. KPIs Financieros (MRR · ARR · ARPU · LTV · Churn · Activas · Trial · Total)
 *   5. KPIs Operativos (Leads · Reservas · Marketplace · Orgs nuevas)
 *   6. Alertas (Churn risk · NPS delta · link drill-down)
 *   7. Weekly Report inline
 *   8. Acciones Rápidas (4 botones → Operations/Centros · Operations/Leads · Operations/Reservas · Intelligence/Funnel)
 *
 * Fuentes (todas existentes · 0 cambios backend):
 *   GET /api/founder-os/home
 *   GET /api/superadmin/north-star/config + POST .../config
 *   GET /api/superadmin/churn-risk
 *   GET /api/superadmin/nps?days=30
 *   GET /api/founder-os/reports/weekly
 */
import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "../../lib/api";
import {
  Crown, TrendUp, TrendDown, ArrowRight, Star, Coins, Users,
  ChartLineUp, Pulse, ArrowsClockwise, Robot, Tray, Buildings,
  Storefront, CalendarBlank, Bell, Warning, ChatCircleDots,
  ChartPieSlice, FileText, Lightning,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function fmtEur(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(2) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "k";
  return Number(n).toFixed(0);
}

const KPI_LABELS = {
  mrr: "MRR",
  active_orgs: "Orgs activas",
  nps_avg: "NPS (30d)",
  chat_conv_rate: "Conv. chat IA",
  bookings_per_day: "Reservas/día",
};

export default function FounderHome() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloading, setReloading] = useState(false);

  // North Star picker
  const [nsConfig, setNsConfig] = useState(null);
  const [nsChanging, setNsChanging] = useState(false);

  // Alertas
  const [churn, setChurn] = useState(null);
  const [nps, setNps] = useState(null);

  // Weekly Report
  const [weekly, setWeekly] = useState(null);
  const [weeklyLoading, setWeeklyLoading] = useState(false);

  const load = async () => {
    setReloading(true);
    try {
      const [home, cfg, ch, np] = await Promise.allSettled([
        api.get("/founder-os/home"),
        api.get("/superadmin/north-star/config"),
        api.get("/superadmin/churn-risk"),
        api.get("/superadmin/nps?days=30"),
      ]);
      if (home.status === "fulfilled") {
        setData(home.value.data);
        setError(null);
      } else {
        setError(home.reason?.response?.data?.detail || "Error al cargar Dashboard Principal");
      }
      if (cfg.status === "fulfilled") setNsConfig(cfg.value.data);
      if (ch.status === "fulfilled") setChurn(ch.value.data);
      if (np.status === "fulfilled") setNps(np.value.data);
    } finally {
      setLoading(false);
      setReloading(false);
    }
  };

  const loadWeekly = async () => {
    setWeeklyLoading(true);
    try {
      const { data } = await api.get("/founder-os/reports/weekly");
      setWeekly(data);
    } catch (e) { console.warn("weekly report unavailable", e?.message); }
    finally { setWeeklyLoading(false); }
  };

  useEffect(() => { load(); loadWeekly(); }, []);

  const changeKpi = async (newKpi) => {
    if (!newKpi || nsChanging) return;
    setNsChanging(true);
    try {
      await api.post("/superadmin/north-star/config", { kpi: newKpi });
      toast.success("KPI norte actualizado");
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "No se pudo actualizar el KPI");
    } finally {
      setNsChanging(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-[var(--ag-text-dim)] text-sm" data-testid="founder-home-loading">
        Cargando Dashboard Principal…
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-8 text-red-400 text-sm" data-testid="founder-home-error">{error}</div>
    );
  }

  const { saas, north_star, funnel_30d, health_score, score_breakdown } = data;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="founder-home">
      {/* Hero */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ag-gold)] font-semibold flex items-center gap-1.5">
            <Crown size={11} weight="fill" /> Founder Control Center · Dashboard Principal
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-2">
            Tu centro de mando ejecutivo
          </h1>
          <p className="text-xs text-[var(--ag-text-dim)] mt-1">
            Métricas en tiempo real · sin mock · una sola fuente de verdad
          </p>
        </div>
        <button
          type="button"
          onClick={() => { load(); loadWeekly(); }}
          disabled={reloading}
          className="inline-flex items-center gap-1.5 text-xs text-[var(--ag-text-dim)] hover:text-[var(--ag-gold)] px-3 py-2 rounded border border-[var(--ag-border)] hover:border-[var(--ag-gold)]/50 transition-colors disabled:opacity-50"
          data-testid="founder-home-refresh"
        >
          <ArrowsClockwise size={13} className={reloading ? "animate-spin" : ""} />
          Refrescar
        </button>
      </div>

      {/* 1 · NORTH STAR (sticky · pegada al topbar al hacer scroll) */}
      <div
        className="sticky top-0 z-10 -mx-6 lg:-mx-8 px-6 lg:px-8 pt-1 pb-3
                   bg-[var(--ag-bg)]/95 backdrop-blur supports-[backdrop-filter]:bg-[var(--ag-bg)]/85"
        data-testid="founder-home-north-star-wrap"
      >
        <div
          className="relative overflow-hidden rounded-lg border border-[var(--ag-gold)]/30
                     bg-gradient-to-br from-[var(--ag-gold)]/10 via-transparent to-[var(--ag-gold)]/5 p-5"
          data-testid="founder-home-north-star"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <div className="text-[9px] uppercase tracking-[0.22em] text-[var(--ag-gold)] font-bold flex items-center gap-1.5">
                <Star size={11} weight="fill" /> Estrella del Norte
              </div>
              <div className="text-xs text-[var(--ag-text-dim)] mt-1">{north_star.label}</div>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="font-display text-5xl font-black tabular-nums tracking-tighter" data-testid="ns-value">
                  {fmtEur(north_star.value)}
                </span>
                <span className="text-sm text-[var(--ag-text-dim)] tabular-nums">
                  / {fmtEur(north_star.target)}
                </span>
              </div>
              <div className="mt-3 h-2 rounded-full bg-[var(--ag-midnight)]/40 overflow-hidden max-w-md">
                <div
                  className="h-full bg-[var(--ag-gold)] transition-all"
                  style={{ width: `${Math.min(100, north_star.progress_pct)}%` }}
                />
              </div>
              <div className="text-[10px] text-[var(--ag-text-muted)] mt-1.5 tabular-nums">
                {north_star.progress_pct}% del objetivo
              </div>
            </div>

            {/* Picker inline · reconfigura sin abandonar el Dashboard (F-02) */}
            {nsConfig && (
              <div className="flex flex-col gap-1.5" data-testid="ns-picker">
                <div className="text-[9px] uppercase tracking-wider text-[var(--ag-text-muted)]">
                  Reconfigurar KPI norte
                </div>
                <div className="flex flex-wrap gap-1.5 max-w-[280px]">
                  {(nsConfig.options || []).map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => changeKpi(k)}
                      disabled={nsChanging}
                      className={`px-2.5 py-1 text-[10px] uppercase tracking-wider rounded-sm border transition-colors disabled:opacity-50 ${
                        nsConfig.kpi === k
                          ? "bg-[var(--ag-gold)] text-black border-transparent font-bold"
                          : "border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-white"
                      }`}
                      data-testid={`ns-pick-${k}`}
                    >
                      {KPI_LABELS[k] || k}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 2 · SALUD PLATAFORMA · Health Score 0-100 + 4 sub-scores */}
      <section
        className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-5"
        data-testid="founder-home-health"
      >
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-[9px] uppercase tracking-[0.22em] text-[var(--ag-text-dim)] font-semibold flex items-center gap-1.5">
              <Pulse size={11} weight="duotone" /> Salud de la plataforma
            </div>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="font-display text-4xl font-black tabular-nums" data-testid="health-value">{health_score}</span>
              <span className="text-xs text-[var(--ag-text-dim)]">/ 100</span>
            </div>
          </div>
          <HealthBadge score={health_score} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <ScoreBar label="Actividad" value={score_breakdown.activity} />
          <ScoreBar label="Crecimiento" value={score_breakdown.growth} />
          <ScoreBar label="Funnel" value={score_breakdown.funnel} />
          <ScoreBar label="Actividad IA" value={score_breakdown.ai} />
        </div>
      </section>

      {/* 3 · KPIs FINANCIEROS */}
      <section data-testid="founder-home-financial">
        <h2 className="text-xs uppercase tracking-[0.2em] text-[var(--ag-text-dim)] font-semibold mb-3 flex items-center gap-2">
          <Coins size={13} weight="duotone" /> KPIs financieros (sin mock)
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="MRR" value={saas.active_orgs === 0 && saas.mrr_eur === 0 ? "Sin datos" : `${fmtEur(saas.mrr_eur)} €`} sublabel="Mes corriente" testid="kpi-mrr" />
          <KpiCard label="ARR" value={saas.active_orgs === 0 && saas.arr_eur === 0 ? "Sin datos" : `${fmtEur(saas.arr_eur)} €`} sublabel="MRR × 12" testid="kpi-arr" />
          <KpiCard label="ARPU" value={saas.active_orgs === 0 ? "Sin datos" : `${fmtEur(saas.arpu_eur)} €`} sublabel="Por org activa" testid="kpi-arpu" />
          <KpiCard label="LTV" value={saas.active_orgs === 0 ? "Sin datos" : `${fmtEur(saas.ltv_eur)} €`} sublabel="ARPU / churn" testid="kpi-ltv" />
          <KpiCard label="Activas" value={saas.active_orgs} sublabel="Orgs pagando" testid="kpi-active" />
          <KpiCard label="Trial" value={saas.trialing} sublabel="En periodo prueba" testid="kpi-trialing" />
          <KpiCard
            label="Churn 30d" value={`${saas.churn_30d_pct}%`}
            sublabel={saas.churn_30d_pct < 5 ? "Saludable" : "Atención"}
            accent={saas.churn_30d_pct < 5 ? "ok" : "warn"}
            testid="kpi-churn"
          />
          <KpiCard label="Total" value={saas.total_orgs} sublabel="Orgs totales en BD" testid="kpi-total-orgs" />
        </div>
      </section>

      {/* 4 · KPIs OPERATIVOS */}
      <section data-testid="founder-home-operational">
        <h2 className="text-xs uppercase tracking-[0.2em] text-[var(--ag-text-dim)] font-semibold mb-3 flex items-center gap-2">
          <ChartLineUp size={13} weight="duotone" /> KPIs operativos (últimos 30 días)
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <OpKpiCard
            icon={Tray} label="Leads" value={funnel_30d.leads}
            sublabel={`Mkt → Lead ${funnel_30d.marketplace_to_lead_pct}%`}
            testid="op-kpi-leads"
          />
          <OpKpiCard
            icon={CalendarBlank} label="Reservas" value={funnel_30d.appointments}
            sublabel="Citas creadas en 30d"
            testid="op-kpi-reservas"
          />
          <OpKpiCard
            icon={Storefront} label="Marketplace" value={funnel_30d.marketplace_views}
            sublabel="Impresiones públicas"
            testid="op-kpi-marketplace"
          />
          <OpKpiCard
            icon={Buildings} label="Orgs nuevas" value={funnel_30d.new_orgs}
            sublabel={`Trial → Sub ${funnel_30d.trial_to_sub_pct}%`}
            testid="op-kpi-new-orgs"
          />
        </div>
      </section>

      {/* 5 · ALERTAS */}
      <AlertsBlock churn={churn} nps={nps} />

      {/* 6 · WEEKLY REPORT inline */}
      <WeeklyReportBlock weekly={weekly} loading={weeklyLoading} onReload={loadWeekly} />

      {/* 7 · ACCIONES RÁPIDAS */}
      <section data-testid="founder-home-quick-actions">
        <h2 className="text-xs uppercase tracking-[0.2em] text-[var(--ag-text-dim)] font-semibold mb-3 flex items-center gap-2">
          <Lightning size={13} weight="duotone" /> Acciones rápidas
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <QuickAction
            to="/founder/operations?sub=centros"
            icon={Buildings} label="Ver Centros"
            sublabel="Organizaciones · Directorio"
            testid="qa-centros"
          />
          <QuickAction
            to="/founder/operations?sub=centros"
            icon={Tray} label="Ver Leads"
            sublabel="Inbox plataforma"
            testid="qa-leads"
          />
          <QuickAction
            to="/founder/operations?sub=reservas"
            icon={CalendarBlank} label="Ver Reservas"
            sublabel="Overview platform-wide"
            testid="qa-reservas"
          />
          <QuickAction
            to="/founder/intelligence?sub=funnel"
            icon={ChartLineUp} label="Ver Funnel"
            sublabel="Conversión por etapa"
            testid="qa-funnel"
          />
        </div>
      </section>

      <p className="text-[10px] text-[var(--ag-text-muted)] text-center pt-4">
        Datos generados {new Date(data.generated_at).toLocaleString("es-ES")} ·
        Fuente: /api/founder-os/home (cached lazy)
      </p>
    </div>
  );
}

// ───────────────────────── Sub-componentes ─────────────────────────

function KpiCard({ label, value, sublabel, accent, testid }) {
  const accentClass =
    accent === "ok" ? "text-emerald-400" : accent === "warn" ? "text-amber-400" :
    accent === "danger" ? "text-red-400" : "text-[var(--ag-text)]";
  return (
    <div className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-3.5"
         data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider text-[var(--ag-text-muted)] font-semibold">{label}</div>
      <div className={`font-display text-2xl font-black tabular-nums mt-1 leading-none ${accentClass}`}>{value}</div>
      <div className="text-[10px] text-[var(--ag-text-dim)] mt-1">{sublabel}</div>
    </div>
  );
}

function OpKpiCard({ icon: Icon, label, value, sublabel, testid }) {
  return (
    <div className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-3.5"
         data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[9px] uppercase tracking-wider text-[var(--ag-text-muted)] font-semibold">{label}</div>
        {Icon && <Icon size={14} weight="duotone" className="text-[var(--ag-gold)]" />}
      </div>
      <div className="font-display text-2xl font-black tabular-nums mt-1 leading-none">{value}</div>
      <div className="text-[10px] text-[var(--ag-text-dim)] mt-1">{sublabel}</div>
    </div>
  );
}

function QuickAction({ to, icon: Icon, label, sublabel, testid, external }) {
  const cls =
    "group rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-4 " +
    "hover:border-[var(--ag-gold)]/50 hover:bg-[var(--ag-gold)]/5 transition-colors flex flex-col gap-1";
  const inner = (
    <>
      <div className="flex items-center justify-between">
        <Icon size={18} weight="duotone" className="text-[var(--ag-gold)]" />
        <ArrowRight size={12} className="text-[var(--ag-text-muted)] group-hover:text-[var(--ag-gold)] transition-colors" />
      </div>
      <div className="font-display text-sm font-bold tracking-tight mt-1">{label}</div>
      <div className="text-[10px] text-[var(--ag-text-dim)]">{sublabel}</div>
    </>
  );
  if (external) {
    return (
      <a href={to} className={cls} data-testid={testid} rel="noopener">{inner}</a>
    );
  }
  return (
    <Link to={to} className={cls} data-testid={testid}>{inner}</Link>
  );
}

function HealthBadge({ score }) {
  let label = "—", cls = "bg-zinc-600/20 text-zinc-300 border-zinc-500/30";
  if (score >= 75) { label = "Excelente"; cls = "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"; }
  else if (score >= 50) { label = "Bueno"; cls = "bg-[var(--ag-gold)]/15 text-[var(--ag-gold)] border-[var(--ag-gold)]/40"; }
  else if (score >= 25) { label = "Mejorable"; cls = "bg-amber-500/15 text-amber-300 border-amber-500/40"; }
  else { label = "Crítico"; cls = "bg-red-500/15 text-red-300 border-red-500/40"; }
  return <span className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border ${cls}`}>{label}</span>;
}

function ScoreBar({ label, value }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  return (
    <div>
      <div className="flex items-baseline justify-between text-[10px] text-[var(--ag-text-dim)]">
        <span className="uppercase tracking-wider">{label}</span>
        <span className="tabular-nums">{value}</span>
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-[var(--ag-midnight)]/40 overflow-hidden">
        <div className="h-full bg-[var(--ag-gold)]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function AlertsBlock({ churn, nps }) {
  const churnItems = (churn?.items || []);
  const highCount = churnItems.filter((c) => c.level === "high").length;
  const medCount = churnItems.filter((c) => c.level === "medium").length;

  const npsScore = nps?.nps;
  const npsTotal = nps?.total ?? 0;
  const npsAlert = npsTotal > 0 && npsScore != null && npsScore < 0;

  const total = highCount + medCount + (npsAlert ? 1 : 0);

  return (
    <section className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-5"
             data-testid="founder-home-alerts">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="text-[9px] uppercase tracking-[0.22em] text-[var(--ag-text-dim)] font-semibold flex items-center gap-1.5">
          <Bell size={11} weight="duotone" /> Alertas críticas
        </div>
        {total === 0 ? (
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border bg-emerald-500/15 text-emerald-300 border-emerald-500/40">
            Todo en orden
          </span>
        ) : (
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border bg-amber-500/15 text-amber-300 border-amber-500/40">
            {total} pendiente{total === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {total === 0 && (
        <p className="text-xs text-[var(--ag-text-dim)]">
          Sin riesgos de churn detectados y NPS estable. Revisa de nuevo más tarde.
        </p>
      )}

      <ul className="space-y-2" data-testid="alerts-list">
        {highCount > 0 && (
          <AlertRow
            severity="high"
            icon={Warning}
            title={`${highCount} centro${highCount === 1 ? "" : "s"} con riesgo alto de churn`}
            description="Sin reservas recientes + caída pronunciada en 30d."
            cta="Investigar"
            to="/founder/intelligence?sub=alertas"
            testid="alert-churn-high"
          />
        )}
        {medCount > 0 && (
          <AlertRow
            severity="medium"
            icon={Warning}
            title={`${medCount} centro${medCount === 1 ? "" : "s"} con riesgo medio`}
            description="Indicadores degradados; vigilancia recomendada."
            cta="Revisar"
            to="/founder/intelligence?sub=alertas"
            testid="alert-churn-medium"
          />
        )}
        {npsAlert && (
          <AlertRow
            severity="medium"
            icon={ChatCircleDots}
            title={`NPS negativo (${npsScore}) con ${npsTotal} respuestas (30d)`}
            description="Promotores < detractores. Revisar comentarios recientes."
            cta="Ver NPS"
            /* DOC · Dependencia legacy pendiente: el dashboard NPS detallado todavía
               vive en SA tab "nps-dashboard". Cuando migre a /founder se actualizará. */
            to="/admin?tab=nps-dashboard"
            external
            testid="alert-nps"
          />
        )}
      </ul>
    </section>
  );
}

function AlertRow({ severity, icon: Icon, title, description, cta, to, external, testid }) {
  const sev = severity === "high"
    ? "border-red-500/40 bg-red-500/5 text-red-300"
    : "border-amber-500/40 bg-amber-500/5 text-amber-300";
  const linkProps = external
    ? { href: to, rel: "noopener" }
    : { to };
  const LinkEl = external ? "a" : Link;
  return (
    <li className={`rounded border ${sev} p-3 flex items-start gap-3`} data-testid={testid}>
      <Icon size={18} weight="duotone" className="mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[var(--ag-text)]">{title}</div>
        <div className="text-[11px] text-[var(--ag-text-dim)] mt-0.5">{description}</div>
      </div>
      <LinkEl {...linkProps}
              className="text-[10px] uppercase tracking-wider px-2 py-1 rounded border border-current hover:bg-current/10 shrink-0">
        {cta} →
      </LinkEl>
    </li>
  );
}

function WeeklyReportBlock({ weekly, loading, onReload }) {
  const period = useMemo(() => {
    if (!weekly?.period) return null;
    return `${new Date(weekly.period.from).toLocaleDateString("es-ES")} → ${new Date(weekly.period.to).toLocaleDateString("es-ES")}`;
  }, [weekly]);

  return (
    <section className="rounded-lg border border-[var(--ag-gold)]/25 bg-[var(--ag-surface)] p-5"
             data-testid="founder-home-weekly">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[9px] uppercase tracking-[0.22em] text-[var(--ag-gold)] font-bold flex items-center gap-1.5">
            <ChartPieSlice size={11} weight="fill" /> Informe semanal · inline
          </div>
          {period && (
            <div className="text-[10px] text-[var(--ag-text-muted)] mt-1" data-testid="weekly-period">{period}</div>
          )}
        </div>
        <button
          type="button"
          onClick={onReload}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)] hover:text-[var(--ag-gold)] px-2.5 py-1 rounded border border-[var(--ag-border)] hover:border-[var(--ag-gold)]/50 disabled:opacity-50"
          data-testid="weekly-refresh"
        >
          <ArrowsClockwise size={11} className={loading ? "animate-spin" : ""} />
          Regenerar
        </button>
      </div>

      {!weekly && loading && (
        <div className="mt-3 text-xs text-[var(--ag-text-dim)]">Generando informe semanal…</div>
      )}
      {!weekly && !loading && (
        <div className="mt-3 text-xs text-[var(--ag-text-dim)]">
          Informe no disponible en este momento. Pulsa Regenerar.
        </div>
      )}

      {weekly && (
        <>
          <div className="grid sm:grid-cols-3 gap-3 mt-4">
            <ReportKpi
              label="Nuevas orgs"
              value={weekly.growth?.new_orgs ?? 0}
              delta={weekly.growth?.delta}
              compare={`vs ${weekly.growth?.previous_period ?? 0} sem. anterior`}
              testid="weekly-new-orgs"
            />
            <ReportKpi label="Leads (7d)" value={weekly.activity?.leads ?? 0} icon={Tray} testid="weekly-leads" />
            <ReportKpi label="Citas (7d)" value={weekly.activity?.appointments ?? 0} icon={CalendarBlank} testid="weekly-appointments" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <ReportKpi label="Servicios facturados" value={weekly.activity?.services ?? 0} testid="weekly-services" />
            <ReportKpi label="Reseñas nuevas" value={weekly.activity?.reviews ?? 0} testid="weekly-reviews" />
          </div>

          {Array.isArray(weekly.top_orgs_by_leads) && weekly.top_orgs_by_leads.length > 0 && (
            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-muted)] font-semibold mb-2">
                Top 5 orgs por leads esta semana
              </div>
              <ol className="space-y-1.5 text-xs" data-testid="weekly-top-orgs">
                {weekly.top_orgs_by_leads.map((o, i) => (
                  <li key={`${o.org}-${i}`} className="flex items-center justify-between border-b border-[var(--ag-border)] py-1.5">
                    <span className="text-[var(--ag-text-dim)]">{i + 1}. {o.org}</span>
                    <span className="font-bold tabular-nums text-[var(--ag-gold)]">{o.leads}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          <div className="mt-4 pt-3 border-t border-[var(--ag-border)] text-[10px] text-[var(--ag-text-muted)]">
            Generado {new Date(weekly.generated_at).toLocaleString("es-ES")} ·
            Endpoint: /api/founder-os/reports/weekly
          </div>
        </>
      )}
    </section>
  );
}

function ReportKpi({ label, value, delta, compare, icon: Icon, testid }) {
  const positive = delta != null && delta >= 0;
  return (
    <div className="rounded border border-[var(--ag-border)] p-3" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider text-[var(--ag-text-muted)] flex items-center gap-1">
        {Icon && <Icon size={10} />} {label}
      </div>
      <div className="font-display text-2xl font-black tabular-nums mt-1">{value}</div>
      {delta != null && (
        <div className={`text-[10px] tabular-nums mt-1 flex items-center gap-1 ${positive ? "text-emerald-400" : "text-red-400"}`}>
          {positive ? <TrendUp size={11} /> : <TrendDown size={11} />}
          {positive ? "+" : ""}{delta}
        </div>
      )}
      {compare && <div className="text-[9px] text-[var(--ag-text-muted)] mt-1">{compare}</div>}
    </div>
  );
}
